import React, { useState, useEffect } from 'react';
import { Search, Copy, Loader2, Ticket, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API } from '../config/apiBase';


export default function CouponsPage() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async (store = '') => {
        setLoading(true);
        try {
            let url = `${API}/coupons`;
            if (store) {
                url += `?store=${encodeURIComponent(store)}`;
            }
            const response = await fetch(url);
            const data = await response.json();
            setCoupons(data.coupons || []);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchCoupons(searchQuery);
    };

    const copyCoupon = async (coupon) => {
        navigator.clipboard.writeText(coupon.code);
        setCopiedId(coupon.coupon_id);
        toast.success('Cupom copiado!');
        
        // Track usage
        try {
            await fetch(`${API}/coupons/${coupon.coupon_id}/use`, { method: 'PUT' });
        } catch (error) {
            console.error('Error tracking coupon use:', error);
        }

        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatDiscount = (coupon) => {
        if (coupon.discount_type === 'percent') {
            return `${coupon.discount_value}% OFF`;
        }
        return `R$ ${coupon.discount_value.toFixed(2)} OFF`;
    };

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="coupons-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <Ticket className="w-8 h-8 text-gold-500" />
                        <h1 className="text-3xl font-outfit font-bold" style={{ color: 'var(--text)' }}>Cupons de Desconto</h1>
                    </div>
                    <p style={{ color: 'var(--muted-2)' }}>Encontre os melhores cupons para economizar em suas compras</p>
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="mb-8">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--muted-2)' }} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Buscar por loja..."
                            className="w-full rounded-lg pl-12 pr-32 py-4 focus:outline-none"
                            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="coupon-search-input"
                        />
                        <Button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 btn-gold px-6">
                            Buscar
                        </Button>
                    </div>
                </form>

                {/* Coupons Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
                    </div>
                ) : coupons.length === 0 ? (
                    <div className="text-center py-20">
                        <Ticket className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted-2)' }} />
                        <p style={{ color: 'var(--muted-2)' }}>Nenhum cupom encontrado</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {coupons.map((coupon, index) => (
                            <div 
                                key={coupon.coupon_id} 
                                className="coupon-card p-5 rounded-xl animate-fade-in"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                data-testid={`coupon-${coupon.coupon_id}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <span className="inline-block text-xs text-gold-500 uppercase tracking-wider mb-2">
                                            {coupon.store_name}
                                        </span>
                                        <p className="font-medium mb-2" style={{ color: 'var(--text)' }}>{coupon.description}</p>
                                        <span className="inline-block bg-gold-500/20 text-gold-400 text-sm font-bold px-3 py-1 rounded">
                                            {formatDiscount(coupon)}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <code className="px-4 py-2 rounded text-gold-500 font-mono text-lg" style={{ background: 'var(--surface-2)' }}>
                                            {coupon.code}
                                        </code>
                                        <Button
                                            onClick={() => copyCoupon(coupon)}
                                            variant="outline"
                                            size="sm"
                                            className={`border-gold-500/50 ${copiedId === coupon.coupon_id ? 'text-green-500 border-green-500/50' : 'text-gold-500'}`}
                                            data-testid={`copy-coupon-${coupon.coupon_id}`}
                                        >
                                            {copiedId === coupon.coupon_id ? (
                                                <>
                                                    <Check className="w-4 h-4 mr-1" />
                                                    Copiado
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-4 h-4 mr-1" />
                                                    Copiar
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                {coupon.uses_count > 0 && (
                                    <p className="text-xs mt-3" style={{ color: 'var(--muted-2)' }}>
                                        Usado {coupon.uses_count} vezes
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
